import { useCallback, useEffect, useRef, useState } from 'react';
import { adaptationApi, type AppendUnusedPreview } from '../../../services/api/adaptation';

export type UnusedBusy = 'remove' | 'undo' | null;
export type UnusedActionResult = 'done' | 'cancelled' | 'skipped';

/**
 * Bản xem trước «mục thừa» + bỏ / hoàn tác (261102 E1-P2, N2 review).
 * - Mọi lần tải (tự động hoặc `reload`) mang một số thứ tự; phản hồi của lần cũ / truyện cũ bị bỏ.
 * - `reload` KHÔNG ném: lỗi ⇒ `error='LOAD_FAILED'`.
 * - `remove` / `undo` dùng một khoá đồng bộ (ref): gọi trùng hoặc gọi chéo ⇒ `'skipped'`; hộp xác nhận (nếu có) nằm TRONG khoá nên chỉ mở một lần; `busy` chỉ bật sau khi người dùng xác nhận (lúc còn hỏi, panel chưa hiện «Đang bỏ…»).
 * - Lỗi API của bỏ/hoàn tác ném lại cho nơi gọi (hiển thị theo mã), khoá luôn được nhả.
 */
export const useAppendUnused = (storyId: string, enabled: boolean, dataVersion: number, refreshAll: () => Promise<unknown>) => {
  const [view, setView] = useState<AppendUnusedPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<UnusedBusy>(null);
  const seq = useRef(0);
  const lock = useRef(false);
  const viewRef = useRef<AppendUnusedPreview | null>(null);
  viewRef.current = view;
  const scope = useRef({ storyId, enabled });
  scope.current = { storyId, enabled };

  /** Tải một lần; chỉ lần MỚI NHẤT (cùng truyện, còn bật) được ghi vào state. */
  const load = useCallback(async (): Promise<void> => {
    const my = ++seq.current;
    const sid = storyId;
    setLoading(true);
    try {
      const v = await adaptationApi.appendUnusedPreview(sid);
      if (my !== seq.current) return;
      setView(v); setError(null);
    } catch {
      if (my !== seq.current) return;
      setError('LOAD_FAILED');
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [storyId]);

  // Đổi truyện / tắt: bỏ ngay view cũ + vô hiệu mọi phản hồi đang bay.
  useEffect(() => { seq.current += 1; setView(null); setError(null); setLoading(false); }, [storyId, enabled]);
  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return () => { seq.current += 1; };
  }, [enabled, dataVersion, load]);

  const reload = useCallback(async (): Promise<void> => { if (!scope.current.enabled) return; await load(); }, [load]);

  const mutate = useCallback(async (kind: 'remove' | 'undo', call: (v: AppendUnusedPreview & { runId: string; modifiedAt: string }) => Promise<unknown>, confirm?: () => Promise<boolean>): Promise<UnusedActionResult> => {
    const v = viewRef.current;
    if (lock.current || !v?.available || !v.runId || !v.modifiedAt) return 'skipped';
    lock.current = true;   // khoá đồng bộ gồm cả lúc HỎI xác nhận (không mở nhiều hộp); `busy` (hiển thị «Đang bỏ…») chỉ bật SAU khi người dùng đồng ý
    try {
      if (confirm && !(await confirm())) return 'cancelled';
      setBusy(kind);
      await call(v as AppendUnusedPreview & { runId: string; modifiedAt: string });
      try { await refreshAll(); } catch { /* lỗi nạp lại workbench: bản xem trước vẫn được tải lại bên dưới */ }
      await load();
      return 'done';
    } finally { lock.current = false; setBusy(null); }
  }, [refreshAll, load]);

  const remove = useCallback((ids: string[], confirm?: () => Promise<boolean>) => mutate('remove', (v) => adaptationApi.appendUnusedRemove({ storyId, runId: v.runId, ids, expectedModifiedAt: v.modifiedAt }), confirm), [mutate, storyId]);
  const undo = useCallback((confirm?: () => Promise<boolean>) => mutate('undo', (v) => adaptationApi.appendUnusedUndo({ storyId, runId: v.runId, expectedModifiedAt: v.modifiedAt }), confirm), [mutate, storyId]);
  return { view, loading, error, busy, reload, remove, undo };
};
